const AUTH_KEYS = [
  'e8t_access_token',
  'e8t_refresh_token',
  'secure_auth_session',
  'supabase.auth.token',
  'sb-auth-token'
]

const PRIOR_USER_STATE_KEYS = [
  'appState_selectedReport',
  'currentActiveTab',
  'currentAnswers',
  'currentAssessmentName',
  'currentAssessmentId',
  'currentMaturityLevel',
  'currentCompliances',
  'currentAssessmentDate',
  'currentBackground',
  'currentScope',
  'currentLimitations',
  'currentSummaryRecommendations',
  'currentHasManualSave',
  'currentSelectedQuestion',
  'currentPanelOpen',
  'currentActiveModalTab'
]

const NON_SENSITIVE_PREFERENCE_KEYS = [
  'appState_activeModule',
  'appState_activeCJSection',
  'appState_sidebarCollapsed',
  'sidebarCollapsed',
  'autoSaveEnabled',
  'lastSeenUpdateVersion',
  'lastViewedUpdateVersion',
  'lastSeenVersion',
  'panelsExpanded',
  'crownJewelsSort',
  'lastActiveModule',
  'lastActiveCJSection'
]

export const RETAINED_LEGACY_DOMAIN_KEYS = Object.freeze([
  'savedAssessments',
  'e8_assessments',
  'e8ReferenceImplementations',
  'securityServicesData',
  'crownJewelClients',
  'crownJewelAssets',
  'crownJewelImportHistory',
  'quickRiskAssessments',
  'vulnerabilityClients',
  'lastSelectedClient',
  'currentUser'
])

export const RETAINED_LEGACY_DOMAIN_NAMESPACES = Object.freeze([
  'vulnerabilityData_<client>'
])

const listeners = new Set()
const channelName = 'e8t-session-invalidation-v1'
let channel = null
let authorityTermination = null

const projectAuthKey = () => {
  try {
    const host = new URL(process.env.REACT_APP_SUPABASE_URL).hostname
    const projectRef = host.split('.')[0]
    return projectRef ? `sb-${projectRef}-auth-token` : null
  } catch {
    return null
  }
}

const exactAuthKeys = () => [...AUTH_KEYS, projectAuthKey()].filter(Boolean)

const removeExactKeys = keys => {
  if (typeof localStorage === 'undefined') return
  keys.forEach(key => localStorage.removeItem(key))
}

const notify = (reason, transition) => {
  listeners.forEach(listener => listener(reason, transition))
}

const ensureChannel = () => {
  if (channel || typeof BroadcastChannel === 'undefined') return channel
  channel = new BroadcastChannel(channelName)
  channel.addEventListener('message', event => {
    if (event.data?.type !== 'session-invalidated') return
    clearBrowserSessionState({ broadcast: false, reason: event.data.reason || 'remote-invalidation', preserveAuthority: false })
  })
  return channel
}

export const clearBrowserAuthState = () => removeExactKeys(exactAuthKeys())

export const shouldPreserveCustomAuthorityOnDirectSignOut = authClient => Boolean(
  authClient?.token && authClient?.userId && authClient?.authoritySource === 'custom'
)

export const clearBrowserSessionState = ({ broadcast = true, notifyListeners = true, preserveAuthority = false, reason = 'signed-out' } = {}) => {
  removeExactKeys([...exactAuthKeys(), ...PRIOR_USER_STATE_KEYS])
  if (broadcast) ensureChannel()?.postMessage({ type: 'session-invalidated', reason })
  if (notifyListeners) notify(reason, { preserveAuthority })
}

const readDirectSession = async directClient => {
  if (!directClient?.auth) return { verified: true, session: null }
  if (typeof directClient.auth.getSession !== 'function') return { verified: false, session: null }
  try {
    const result = await directClient.auth.getSession()
    if (result?.error) return { verified: false, session: null }
    return { verified: true, session: result?.data?.session ?? null }
  } catch {
    return { verified: false, session: null }
  }
}

export const failClosedBrowserAuthority = () => {
  clearBrowserAuthState()
  if (typeof window !== 'undefined' && typeof window.location?.reload === 'function') {
    window.location.reload()
  }
}

export const clearDirectBrowserAuthority = async ({ directClient, failClosed } = {}) => {
  if (!directClient?.auth) {
    clearBrowserAuthState()
    return true
  }

  let signOutFailed = false
  try {
    const result = await directClient.auth.signOut?.({ scope: 'local' })
    signOutFailed = Boolean(result?.error)
  } catch {
    signOutFailed = true
  }

  let verification = await readDirectSession(directClient)
  if (signOutFailed || !verification.verified || verification.session) {
    try {
      // auth-js 2.x has no public local-only removal method after signOut returns
      // an error. Feature-detect its installed local session remover and verify it.
      await directClient.auth._removeSession?.()
    } catch {
      // Verification below decides whether fail-closed handling is required.
    }
    clearBrowserAuthState()
    verification = await readDirectSession(directClient)
  }

  if (!verification.verified || verification.session) {
    ;(failClosed || failClosedBrowserAuthority)()
    throw new Error('Direct browser authority removal could not be verified')
  }
  return true
}

export const terminateBrowserAuthority = async ({ authClient, directClient, manager, failClosed }) => {
  if (authorityTermination) return authorityTermination
  authorityTermination = (async () => {
    authClient?.clearMemoryTokens?.()
    manager?.cleanup?.()
    await clearDirectBrowserAuthority({ directClient, failClosed })
  })()
  try {
    await authorityTermination
  } finally {
    authorityTermination = null
  }
}

export const reconcileBrowserIdentity = async ({ nextUserId, authClient, directClient, directEventSession, manager, failClosed }) => {
  if (!nextUserId || authClient?.userId !== nextUserId) {
    throw new Error('The new browser identity has not been verified')
  }
  if (directEventSession !== undefined) {
    if (!directEventSession?.user?.id || directEventSession.user.id !== nextUserId) {
      ;(failClosed || failClosedBrowserAuthority)()
      throw new Error('The direct auth event identity has not been verified')
    }
    manager?.start?.()
    return true
  }
  const verification = await readDirectSession(directClient)
  if (!verification.verified || (verification.session?.user?.id && verification.session.user.id !== nextUserId)) {
    await clearDirectBrowserAuthority({ directClient, failClosed })
  }
  manager?.start?.()
  return true
}

export const onBrowserSessionInvalidated = listener => {
  listeners.add(listener)
  ensureChannel()
  return () => listeners.delete(listener)
}

export const getRegisteredBrowserState = () => ({
  authentication: exactAuthKeys(),
  priorUserState: [...PRIOR_USER_STATE_KEYS],
  nonSensitivePreferences: [...NON_SENSITIVE_PREFERENCE_KEYS],
  retainedLegacyDomain: [...RETAINED_LEGACY_DOMAIN_KEYS],
  retainedLegacyNamespaces: [...RETAINED_LEGACY_DOMAIN_NAMESPACES]
})
